import { useState } from 'react';
import {
  getAffordableChanges,
  getHasAffordableChanged,
  getValidationErrorMessages,
} from './utils';
import { getIsAddRowDisabled } from './IncomeEligibility/utils';
import {
  AFFORDABLE,
  ENABLED,
  INCOME_ELIGIBILITY,
  MAXIMUM_INCOME,
  OCCUPANTS,
} from './constants';

const hasAnyValidationError = (validationErrorMessages) =>
  Object.entries(validationErrorMessages).some(([key, value]) => {
    if (key === INCOME_ELIGIBILITY) {
      return (value ?? []).some((rowError) =>
        Object.values(rowError ?? {}).some(Boolean),
      );
    }
    return Boolean(value);
  });

export const useAffordable = ({ affordable, updateProperty }) => {
  const [affordableTabData, setAffordableTabData] = useState(affordable);
  const [validationErrorMessages, setValidationErrorMessages] = useState({});

  const handleOnUndo = () => {
    setAffordableTabData(affordable);
    setValidationErrorMessages({});
  };

  const updateAffordable = (updates) => {
    setAffordableTabData((prevState) => ({ ...prevState, ...updates }));
  };

  const clearFieldErrorMessage = (field) => {
    setValidationErrorMessages((prevState) => ({
      ...prevState,
      [field]: '',
    }));
  };

  const updateEnabled = (value) => {
    setAffordableTabData((prevState) => {
      if (!value) {
        return { ...prevState, ...affordable, [ENABLED]: value };
      }
      return { ...prevState, [ENABLED]: value };
    });

    if (!value) {
      setValidationErrorMessages({});
    }
  };

  const addEligibilityRow = () => {
    setAffordableTabData((prevState) => {
      if (getIsAddRowDisabled(prevState[INCOME_ELIGIBILITY])) {
        return prevState;
      }
      return {
        ...prevState,
        [INCOME_ELIGIBILITY]: [
          ...prevState[INCOME_ELIGIBILITY],
          { [MAXIMUM_INCOME]: '', [OCCUPANTS]: '' },
        ],
      };
    });
  };

  const updateEligibilityRow = (index) => (updates) => {
    setAffordableTabData((prevState) => {
      const rows = prevState[INCOME_ELIGIBILITY].map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...updates } : row,
      );
      return { ...prevState, [INCOME_ELIGIBILITY]: rows };
    });
  };

  const deleteEligibilityRow = (index) => {
    setAffordableTabData((prevState) => ({
      ...prevState,
      [INCOME_ELIGIBILITY]: prevState[INCOME_ELIGIBILITY].filter(
        (_row, rowIndex) => rowIndex !== index,
      ),
    }));
    setValidationErrorMessages((prevState) => {
      const rowErrors = prevState[INCOME_ELIGIBILITY];
      if (!rowErrors) {
        return prevState;
      }
      return {
        ...prevState,
        [INCOME_ELIGIBILITY]: rowErrors.filter(
          (_row, rowIndex) => rowIndex !== index,
        ),
      };
    });
  };

  const clearEligibilityRowError = (index) => (field) => {
    setValidationErrorMessages((prevState) => {
      const rowErrors = prevState[INCOME_ELIGIBILITY];
      if (!rowErrors?.[index]?.[field]) {
        return prevState;
      }
      const updatedRowErrors = rowErrors.map((rowError, rowIndex) =>
        rowIndex === index ? { ...rowError, [field]: '' } : rowError,
      );
      return { ...prevState, [INCOME_ELIGIBILITY]: updatedRowErrors };
    });
  };

  const validate = () => {
    const messages = getValidationErrorMessages(affordableTabData);
    setValidationErrorMessages(messages ?? {});
    return !messages;
  };

  const handleOnSave = async () => {
    if (!validate()) {
      return;
    }
    if (!updateProperty) {
      return;
    }
    const changes = getAffordableChanges(affordable, affordableTabData);
    updateProperty(changes, { [AFFORDABLE]: affordableTabData });
  };

  const isActionsDisabled =
    !getHasAffordableChanged(affordable, affordableTabData) ||
    hasAnyValidationError(validationErrorMessages);

  return {
    addEligibilityRow,
    affordableTabData,
    clearEligibilityRowError,
    clearFieldErrorMessage,
    deleteEligibilityRow,
    handleOnSave,
    handleOnUndo,
    isActionsDisabled,
    updateAffordable,
    updateEligibilityRow,
    updateEnabled,
    validationErrorMessages,
  };
};
